import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faEye, faEyeSlash } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { useRouter } from "vue-router/composables";
import { getGalaxyInstance } from "@/app";
import { useGlobalUploadModal } from "@/composables/globalUploadModal";
import { getAppRoot } from "@/onload/loadConfig";
import { useToolStore } from "@/stores/toolStore";
import localize from "@/utils/localization";
import { filterTools, getValidPanelItems, getValidToolsInCurrentView, getValidToolsInEachSection } from "./utilities";
import ToolSearch from "./Common/ToolSearch.vue";
import ToolSection from "./Common/ToolSection.vue";
import UploadButton from "@/components/Upload/UploadButton.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'ToolBox',
    props: {
        workflow: { type: Boolean, default: false },
        panelView: { type: String, required: true },
        showAdvanced: { type: Boolean, default: false, required: true },
        panelQuery: { type: String, required: true },
        editorWorkflows: { type: Array, default: null },
        dataManagers: { type: Array, default: null },
        moduleSections: { type: Array, default: null },
    },
    emits: ["update:show-advanced", "update:panel-query", "onInsertTool", "onInsertModule", "onInsertWorkflow", "onInsertWorkflowSteps"],
    setup(__props, { emit }) {
        const props = __props;
        const SECTION_IDS_TO_EXCLUDE = ["expression_tools"]; // if this isn't the Workflow Editor panel
        const { openGlobalUploadModal } = useGlobalUploadModal();
        const router = useRouter();
        library.add(faEye, faEyeSlash);
        const queryFilter = ref(null);
        const queryPending = ref(false);
        const showSections = ref(props.workflow);
        const results = ref([]);
        const resultPanel = ref(null);
        const buttonText = ref("");
        const buttonIcon = ref("");
        const closestTerm = ref(null);
        const toolStore = useToolStore();
        const propShowAdvanced = computed({
            get: () => {
                return props.showAdvanced;
            },
            set: (val) => {
                emit("update:show-advanced", val);
            },
        });
        const query = computed({
            get: () => {
                return props.panelQuery;
            },
            set: (q) => {
                queryPending.value = true;
                emit("update:panel-query", q);
            },
        });
        const { currentPanel } = storeToRefs(toolStore);
        const hasResults = computed(() => results.value.length > 0);
        const queryTooShort = computed(() => query.value && query.value.length < 3);
        const queryFinished = computed(() => query.value && queryPending.value != true);
        const hasDataManagerSection = computed(() => props.workflow && props.dataManagers && props.dataManagers.length > 0);
        const dataManagerSection = computed(() => {
            return {
                name: localize("Data Managers"),
                elems: props.dataManagers,
            };
        });
        /** `toolsById` from `toolStore`, except it only has valid tools for `props.workflow` value */
        const localToolsById = computed(() => {
            if (toolStore.toolsById && Object.keys(toolStore.toolsById).length > 0) {
                return getValidToolsInCurrentView(toolStore.toolsById, props.workflow, !props.workflow ? SECTION_IDS_TO_EXCLUDE : []);
            }
            return {};
        });
        /** `currentPanel` from `toolStore`, except it only has valid tools and sections for `props.workflow` value */
        const localSectionsById = computed(() => {
            const validToolIdsInCurrentView = Object.keys(localToolsById.value);
            // Looking within each `ToolSection`, and filtering on child elements
            const sectionEntries = getValidToolsInEachSection(validToolIdsInCurrentView, currentPanel.value);
            // Looking at each item in the panel now (not within each child)
            return getValidPanelItems(sectionEntries, validToolIdsInCurrentView, !props.workflow ? SECTION_IDS_TO_EXCLUDE : []);
        });
        const toolsList = computed(() => Object.values(localToolsById.value));
        /**
         * If not searching or no results, we show all tools in sections (default)
         *
         * If we have results for search, we show tools in sections or just tools,
         * based on whether `showSections` is true or false
         */
        const localPanel = computed(() => {
            if (hasResults.value) {
                if (showSections.value) {
                    return resultPanel.value;
                }
                else {
                    return filterTools(localToolsById.value, results.value);
                }
            }
            else {
                return localSectionsById.value;
            }
        });
        const favWorkflows = computed(() => {
            const Galaxy = getGalaxyInstance();
            const storedWorkflowMenuEntries = Galaxy && Galaxy.config.stored_workflow_menu_entries;
            if (storedWorkflowMenuEntries) {
                const returnedWfs = [];
                if (!props.workflow) {
                    returnedWfs.push({
                        title: localize("All workflows"),
                        href: `${getAppRoot()}workflows/list`,
                        id: "list",
                    });
                }
                const storedWfs = [
                    ...storedWorkflowMenuEntries.map((menuEntry) => {
                        return {
                            id: menuEntry.id,
                            title: menuEntry.name,
                            href: `${getAppRoot()}workflows/run?id=${menuEntry.id}`,
                        };
                    }),
                ];
                return returnedWfs.concat(storedWfs);
            }
            else {
                return [];
            }
        });
        const workflowSection = computed(() => {
            if (props.workflow && props.editorWorkflows.length > 0) {
                return {
                    name: localize("Workflows"),
                    elems: props.workflow && props.editorWorkflows,
                };
            }
            else {
                return null;
            }
        });
        function onInsertModule(module, event) {
            event.preventDefault();
            emit("onInsertModule", module.name, module.title);
        }
        function onInsertWorkflow(workflow, event) {
            event.preventDefault();
            emit("onInsertWorkflow", workflow.latest_id, workflow.name);
        }
        function onInsertWorkflowSteps(workflow) {
            emit("onInsertWorkflowSteps", workflow.id, workflow.step_count);
        }
        function onToolClick(tool, evt) {
            if (!props.workflow) {
                if (tool.id === "upload1") {
                    evt.preventDefault();
                    openGlobalUploadModal();
                }
                else if (tool.form_style === "regular") {
                    evt.preventDefault();
                    // encode spaces in tool.id
                    const toolId = tool.id;
                    router.push(`/?tool_id=${encodeURIComponent(toolId)}&version=latest`);
                }
            }
            else {
                evt.preventDefault();
                emit("onInsertTool", tool.id, tool.name);
            }
        }
        function onResults(idResults, sectioned, closestMatch = null) {
            if (idResults !== null && idResults.length > 0) {
                results.value = idResults;
                resultPanel.value = sectioned;
                if (sectioned === null) {
                    showSections.value = false;
                }
            }
            else {
                results.value = [];
                resultPanel.value = null;
            }
            closestTerm.value = closestMatch;
            queryFilter.value = hasResults.value ? query.value : null;
            setButtonText();
            queryPending.value = false;
        }
        function onToggle() {
            showSections.value = !showSections.value;
            setButtonText();
        }
        function setButtonText() {
            buttonText.value = showSections.value ? localize("Hide Sections") : localize("Show Sections");
            buttonIcon.value = showSections.value ? "fa-eye-slash" : "fa-eye";
        }
        return { __sfc: true, SECTION_IDS_TO_EXCLUDE, openGlobalUploadModal, router, emit, props, queryFilter, queryPending, showSections, results, resultPanel, buttonText, buttonIcon, closestTerm, toolStore, propShowAdvanced, query, currentPanel, hasResults, queryTooShort, queryFinished, hasDataManagerSection, dataManagerSection, localToolsById, localSectionsById, toolsList, localPanel, favWorkflows, workflowSection, onInsertModule, onInsertWorkflow, onInsertWorkflowSteps, onToolClick, onResults, onToggle, setButtonText, FontAwesomeIcon, localize, ToolSearch, ToolSection, UploadButton };
    }
});
